import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  TOOL_METHODS,
  emptyApiToolDraft,
  toolToDraft,
  validateApiToolDraft,
  type ApiToolDraft,
} from "../utils/apiTools";
import type { IntegrationTool, IntegrationToolMethod } from "../types/integration";

interface IntegrationToolDialogProps {
  isOpen: boolean;
  /** `null` = tool nova. */
  tool: IntegrationTool | null;
  existing: IntegrationTool[];
  isSaving: boolean;
  onCancel: () => void;
  onSave: (draft: ApiToolDraft) => void;
}

/**
 * Adicionar ou editar uma tool de conector `api` (AB#87542): nome, descrição, método,
 * endpoint e o JSON Schema dos parâmetros. O schema é validado como JSON antes de gravar —
 * o diálogo não deixa sair um objeto que o runtime não consegue usar.
 */
export const IntegrationToolDialog = ({
  isOpen,
  tool,
  existing,
  isSaving,
  onCancel,
  onSave,
}: IntegrationToolDialogProps) => {
  const { t } = useTranslation();
  // O diálogo é montado por edição (o pai o desmonta ao fechar): o estado inicial basta.
  const [draft, setDraft] = useState<ApiToolDraft>(() => (tool ? toolToDraft(tool) : emptyApiToolDraft()));
  const [touched, setTouched] = useState(false);

  const problems = useMemo(
    () => validateApiToolDraft(draft, existing, tool?.name),
    [draft, existing, tool],
  );
  const problemOf = (...keys: string[]) => (touched ? problems.find((problem) => keys.includes(problem)) : undefined);

  const update = (patch: Partial<ApiToolDraft>) => setDraft((atual) => ({ ...atual, ...patch }));

  const handleSave = () => {
    setTouched(true);
    if (problems.length > 0) return;
    onSave(draft);
  };

  const nameProblem = problemOf("nameRequired", "nameInvalid", "nameTaken");
  const endpointProblem = problemOf("endpointRequired");
  const schemaProblem = problemOf("schemaInvalidJson", "schemaNotObject");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="flex max-h-[85vh] max-w-xl flex-col">
        <DialogHeader>
          <DialogTitle>{tool ? t("integrations.tools.editTitle") : t("integrations.tools.addTitle")}</DialogTitle>
          <DialogDescription>{t("integrations.tools.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="scroll-slim min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="tool-name">
              {t("integrations.tools.columns.name")}
              <span className="ml-1 text-destructive">*</span>
            </label>
            <Input
              id="tool-name"
              value={draft.name}
              onChange={(e) => update({ name: e.target.value })}
              className="font-mono text-xs"
              placeholder="get_issue"
            />
            {nameProblem && <p className="text-xs text-destructive">{t(`integrations.tools.problems.${nameProblem}`)}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="tool-description">
              {t("integrations.tools.columns.description")}
            </label>
            <Textarea
              id="tool-description"
              value={draft.description}
              onChange={(e) => update({ description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-[8rem_1fr] gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("integrations.tools.columns.method")}</label>
              <Select value={draft.method} onValueChange={(value) => update({ method: value as IntegrationToolMethod })}>
                <SelectTrigger className="font-mono text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOOL_METHODS.map((method) => (
                    <SelectItem key={method} value={method} className="font-mono text-xs">
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="tool-endpoint">
                {t("integrations.tools.endpoint")}
                <span className="ml-1 text-destructive">*</span>
              </label>
              <Input
                id="tool-endpoint"
                value={draft.endpoint}
                onChange={(e) => update({ endpoint: e.target.value })}
                className="font-mono text-xs"
                placeholder="/issues/{id}"
              />
              {endpointProblem && (
                <p className="text-xs text-destructive">{t(`integrations.tools.problems.${endpointProblem}`)}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="tool-schema">
              {t("integrations.tools.inputSchema")}
            </label>
            <Textarea
              id="tool-schema"
              value={draft.inputSchema}
              onChange={(e) => update({ inputSchema: e.target.value })}
              rows={10}
              className="font-mono text-xs"
              spellCheck={false}
            />
            {schemaProblem ? (
              <p className="text-xs text-destructive">{t(`integrations.tools.problems.${schemaProblem}`)}</p>
            ) : (
              <p className="text-xs text-muted-foreground">{t("integrations.tools.inputSchemaHint")}</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            {t("integrations.actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {t("integrations.actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
